import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  Search,
  Filter,
  DollarSign,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Building2,
  User,
  Users,
  ChevronDown,
  ChevronUp,
  X,
  Plus,
  Trash2,
  RefreshCw,
  Paperclip,
  MessageSquare,
  ExternalLink,
  Mail,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import ContractFormFields from './contracts/ContractFormFields';
import CommissionPayoutsPanel from './CommissionPayoutsPanel';
import PaginationControls from './common/PaginationControls';
import API_URL from '../lib/apiConfig';
import {
  PROJECT_TYPES,
  METHODOLOGY_LABELS,
  METHODOLOGY_HINTS,
  METHODOLOGY_DEFAULTS,
  buildMethodologyConfig,
} from '../lib/projectMethodology';
import {
  fetchAccruedRevenue,
  buildPlanPayload,
  createAndCommitPlan,
  committedToast,
} from '../lib/commissionActions';

const API = API_URL;
const fmt = (n) =>
  `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DealManagement() {
  const { user } = useAuth();
  const { hasPermission, hasAnyPermission, isAdmin } = usePermissions();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  // Debounced search + pagination for the deals list (all client-side).
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [defaultResourceTypes, setDefaultResourceTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    account_id: '',
    deal_owner_id: '',
    agent_of_record_id: '',
    contract_status: 'all', // 'all' | 'with_contract' | 'no_contract'
    commission_status: 'all', // 'all' | 'with_commission' | 'no_commission'
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [quickFilter, setQuickFilter] = useState('all'); // 'all' | 'needs_both' | 'needs_contract' | 'needs_commission' | 'complete'

  // Assign Contract Modal
  const [assignContractDeal, setAssignContractDeal] = useState(null);
  const [assignMode, setAssignMode] = useState('new'); // 'new' | 'existing'
  const [assignForm, setAssignForm] = useState({ contract_id: '' });
  const [assignLoading, setAssignLoading] = useState(false);
  const [newContractForm, setNewContractForm] = useState({
    name: '',
    service_category: '',
    contract_type: 'boh',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    total_hours: '',
    hourly_rate: '',
    monthly_rate: '',
    billing_day: 1,
    total_amount: '',
    milestones: [],
    billing_period: 'biweekly',
    invoice_terms: 'net_30',
    notes: '',
    resource_types: [],
    resources: [],
    project_ids: [],
    _accountSearch: '',
    _showAccountDropdown: false,
  });

  // Projects available to link in the Assign Contract dialog (Ryan, 26 Jun:
  // a contract must link at least one project before it can be created).
  const [assignProjects, setAssignProjects] = useState([]);
  // True while the deal's Sales Ops draft is being composed/fetched, so the
  // form can show a "loading" hint instead of looking blank or stale.
  const [draftLoading, setDraftLoading] = useState(false);
  // A human-readable reason shown when the form couldn't be prefilled (e.g. the
  // deal's SOW is still in Draft), so a blank form explains itself.
  const [draftNotice, setDraftNotice] = useState(null);
  // Inline "create new project" inside the Create New Contract dialog
  // (Ryan, 30 Jun): let a user mint a project without leaving the flow.
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  // Methodology for the inline project. Previously hardcoded to waterfall,
  // which meant a project created from Assign Contract could never be an
  // Agile board or a Waves & Threads project without being recreated from
  // the Projects module (Ryan, Jul 2026).
  const [newProjectType, setNewProjectType] = useState('waterfall');
  const [methodologyFields, setMethodologyFields] = useState({
    ...METHODOLOGY_DEFAULTS,
  });

  // Commission Modal
  const [commissionDeal, setCommissionDeal] = useState(null);
  const [commissionTemplates, setCommissionTemplates] = useState([]);
  const [commissionForm, setCommissionForm] = useState({
    deal_id: '',
    template_id: '',
    revenue: '',
    revenue_source: 'manual',
    cost_type: 'percentage',
    cost_value: 50,
    tiers: [
      { tier_number: 1, tier_name: 'Top Line', percentage: 10, users: [] },
      { tier_number: 2, tier_name: 'Override', percentage: 5, users: [] },
      { tier_number: 3, tier_name: 'Sales Rep', percentage: 15, users: [] },
    ],
  });
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Actual delivered revenue for the deal whose commission modal is open.
  // Sourced from the contract time ledger via the accrued-revenue endpoint.
  const [accruedRevenue, setAccruedRevenue] = useState(null);
  const [accruedLoading, setAccruedLoading] = useState(false);
  const [dataFilter, setDataFilter] = useState('real'); // 'test' | 'real' | 'all'

  const canEdit = isAdmin() || hasAnyPermission(['deals.edit.all', 'deals.edit.own']);
  const canSync = isAdmin() || hasPermission('deals.sync');
  const canDelete = isAdmin() || hasPermission('deals.delete');

  // Modal behavior: Escape to close + body scroll lock when any modal is open
  const anyModalOpen = Boolean(assignContractDeal || commissionDeal);
  useEffect(() => {
    if (!anyModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (assignContractDeal) setAssignContractDeal(null);
      else if (commissionDeal) setCommissionDeal(null);
    };
    window.addEventListener('keydown', onKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prev;
    };
  }, [anyModalOpen, assignContractDeal, commissionDeal]);

  const loadDeals = useCallback(async () => {
    try {
      let qs = '';
      if (dataFilter === 'test') qs = '?test_only=true';
      else if (dataFilter === 'real') qs = '?test_only=false';
      // if dataFilter === 'all', no query string

      const res = await api.get(`${API}/api/contracts/deal-pipeline${qs}`);
      if (res.ok) {
        const data = await res.json();
        // Combine all deals
        const allDeals = [...(data.unassigned?.items || []), ...(data.assigned?.items || [])];
        setDeals(allDeals);
      }
    } catch (err) {
      toast.error('Failed to load deals');
    }
  }, [dataFilter]);

  const loadAccounts = useCallback(async () => {
    try {
      const res = await api.get(`${API}/api/accounts?limit=1000`);
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load accounts');
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get(`${API}/api/admin/users`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data || []);
      }
    } catch (err) {
      console.error('Failed to load users');
    }
  }, []);

  const loadContracts = useCallback(async () => {
    try {
      const res = await api.get(`${API}/api/contracts`);
      if (res.ok) {
        const data = await res.json();
        setContracts(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load contracts');
    }
  }, []);

  const loadResourceTypes = useCallback(async () => {
    try {
      const res = await api.get(`${API}/api/admin/default-resource-types`);
      if (res.ok) {
        const data = await res.json();
        const types = (data.default_resource_types || []).filter((t) => t.is_active);
        // Add "Custom" option at the end
        setDefaultResourceTypes([
          ...types,
          { name: 'Custom', sell_rate: 0, description: 'Define your own' },
        ]);
      }
    } catch (err) {
      console.error('Failed to load resource types', err);
    }
  }, []);

  const loadCommissionTemplates = useCallback(async () => {
    try {
      const res = await api.get(`${API}/api/commission-management/templates`);
      if (res.ok) {
        const data = await res.json();
        setCommissionTemplates(data.items || []); // Backend returns "items" not "templates"
      }
    } catch (err) {
      console.error('Failed to load commission templates:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDeals(),
        loadAccounts(),
        loadUsers(),
        loadContracts(),
        loadResourceTypes(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [loadDeals, loadAccounts, loadUsers, loadContracts, loadResourceTypes]);

  useEffect(() => {
    loadData();
    loadCommissionTemplates();
  }, [loadData, loadCommissionTemplates]);

  // Pre-populate newContractForm when a deal is selected for contract assignment
  useEffect(() => {
    if (assignContractDeal) {
      // Reset the form for THIS deal. Crucially, clear the SOW-derived scope
      // fields back to defaults (not `...prev`) so the previously-opened deal's
      // Sales Ops data can't linger while — or if — this deal's draft loads.
      // The draft fetch below then fills them fresh for this deal.
      setNewContractForm((prev) => ({
        ...prev,
        name: assignContractDeal.name || assignContractDeal.deal_name || '',
        account_id: assignContractDeal.account_id || '',
        account_name: assignContractDeal.account_name || '',
        notes: `Created from deal: ${assignContractDeal.name || assignContractDeal.deal_name || ''}`,
        project_ids: [],
        // --- cleared scope (defaults mirror the initial useState) ---
        service_category: '',
        contract_type: 'boh',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        total_hours: '',
        hourly_rate: '',
        monthly_rate: '',
        total_amount: '',
        milestones: [],
        billing_period: 'biweekly',
        invoice_terms: 'net_30',
        resource_types: [],
        resources: [],
        line_items: [],
      }));
      setShowNewProject(false);
      setNewProjectName('');
      setDraftLoading(true);
      setDraftNotice(null);

      // Prepopulate the contract SCOPE from the deal's source-opportunity SOW
      // data (Ryan, 8 Jul: "come to them prepopulated with any and all data
      // from Sales Operations"). Composed server-side without persisting, using
      // the same builder the old auto-create used. Non-fatal — if it fails the
      // form simply stays manual.
      let cancelled = false;
      const activeDealId = assignContractDeal.deal_id || assignContractDeal.zoho_deal_id;
      (async () => {
        try {
          const res = await api.get(`${API}/api/contracts/deal-draft/${activeDealId}`);
          if (!res.ok) return;
          const data = await res.json();
          // Staleness guard: if the user switched to another deal (or closed the
          // form) before this resolved, drop the response so it can't overwrite
          // the current deal's form with wrong-deal data.
          if (cancelled) return;
          const currentDealId = assignContractDeal
            && (assignContractDeal.deal_id || assignContractDeal.zoho_deal_id);
          if (currentDealId !== activeDealId) return;
          const draft = (data.drafts || [])[0];
          if (!draft) {
            // Nothing to prefill — explain why instead of a silent blank form.
            const skipped = data.skipped || [];
            if (!data.source_opportunity_id) {
              setDraftNotice(
                "This deal wasn't created from an opportunity, so there's no Sales Ops data to auto-fill. Enter the contract details manually below.",
              );
            } else if (skipped.length) {
              setDraftNotice(
                "This deal's Sales Ops SOW isn't marked Complete yet, so there's nothing to auto-fill. Mark the SOW Complete in Sales Ops (drag it to the Complete column), or enter the contract details manually below.",
              );
            } else {
              setDraftNotice(
                'No completed Sales Ops scope was found for this deal. Enter the contract details manually below.',
              );
            }
            return;
          }
          setDraftNotice(null);
          setNewContractForm((prev) => ({
            ...prev,
            service_category: draft.service_category || prev.service_category,
            contract_type: draft.contract_type || prev.contract_type,
            start_date: draft.start_date || prev.start_date,
            end_date: draft.end_date || prev.end_date,
            billing_period: draft.billing_period || prev.billing_period,
            invoice_terms: draft.invoice_terms || prev.invoice_terms,
            resource_types: (draft.resource_types && draft.resource_types.length)
              ? draft.resource_types : prev.resource_types,
            resources: (draft.resources && draft.resources.length)
              ? draft.resources : prev.resources,
            milestones: (draft.milestones && draft.milestones.length)
              ? draft.milestones : prev.milestones,
            line_items: draft.line_items || prev.line_items || [],
            total_amount: draft.fixed_amount || prev.total_amount,
          }));
          if ((data.sow_count || 0) > 1) {
            toast((t) => `This deal has ${data.sow_count} SOWs — prefilled the first; the others may need separate contracts.`);
          }
        } catch (e) {
          /* non-fatal: leave the form for manual entry */
        } finally {
          // Only clear the indicator if this is still the active deal's fetch,
          // so a late response from a previous deal doesn't flip it off early.
          const stillActive = assignContractDeal
            && (assignContractDeal.deal_id || assignContractDeal.zoho_deal_id) === activeDealId;
          if (stillActive) setDraftLoading(false);
        }
      })();

      // Load projects so the user can link at least one to the contract.
      (async () => {
        try {
          const res = await api.get(`${API}/api/projects/projects`);
          if (res.ok) {
            const data = await res.json();
            const list = Array.isArray(data) ? data : data.items || data.projects || [];
            setAssignProjects(list);
          } else {
            setAssignProjects([]);
          }
        } catch (e) {
          setAssignProjects([]);
        }
      })();

      // Cleanup: mark this run stale so a late draft response from a
      // previous deal can't overwrite the current form (race guard).
      return () => { cancelled = true; };
    }
  }, [assignContractDeal]);

  const handleSyncDeals = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`${API}/api/integrations/sync/zoho/deals`);
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Deals synced successfully');
        // Reload deals to show updated data
        await loadDeals();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to sync deals');
      }
    } catch (err) {
      toast.error('Sync failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSyncing(false);
    }
  };

  const openCommissionModal = (deal) => {
    setCommissionDeal(deal);
    const dealId = deal.deal_id || deal.zoho_deal_id;
    setCommissionForm({
      deal_id: dealId,
      template_id: '',
      revenue: deal.amount || '',
      revenue_source: 'manual',
      cost_type: 'percentage',
      cost_value: 50,
      tiers: [
        { tier_number: 1, tier_name: 'Top Line', percentage: 10, users: [] },
        { tier_number: 2, tier_name: 'Override', percentage: 5, users: [] },
        { tier_number: 3, tier_name: 'Sales Rep', percentage: 15, users: [] },
      ],
    });
    // Pull the deal's actual delivered revenue from the contract ledger so the
    // plan can be based on real earned revenue rather than a typed figure.
    setAccruedRevenue(null);
    setAccruedLoading(true);
    fetchAccruedRevenue(dealId)
      .then((data) => {
        setAccruedRevenue(data);
        // Default to actuals when real revenue has been delivered.
        if (data && Number(data.revenue) > 0) {
          setCommissionForm((f) => ({
            ...f,
            revenue_source: 'contract_actuals',
            revenue: data.revenue,
          }));
        }
      })
      .finally(() => setAccruedLoading(false));
    // Templates are already loaded on mount
  };

  const saveCommissionPlan = async () => {
    const useActuals = commissionForm.revenue_source === 'contract_actuals';

    // With actuals the revenue comes from the ledger; only require a manual
    // figure when the user has chosen to type one in.
    if (!useActuals && (!commissionForm.revenue || parseFloat(commissionForm.revenue) <= 0)) {
      toast.error('Enter deal revenue, or switch to actual delivered revenue');
      return;
    }

    const hasUsers = commissionForm.tiers.some(
      (t) => t.users && t.users.length > 0 && t.users.some((u) => u.user_id),
    );
    if (!hasUsers) {
      toast.error('Please add at least one user to receive commission');
      return;
    }

    setCommissionLoading(true);
    try {
      // Shared with the Contracts page: create the plan, then commit it (lock +
      // generate the per-user payout records finance pays against).
      const result = await createAndCommitPlan(buildPlanPayload(commissionForm));
      toast.success(committedToast(result.payouts_generated));
      setCommissionDeal(null);
      loadDeals(); // Refresh deals
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCommissionLoading(false);
    }
  };

  const handleCreateInlineProject = async ({ silent = false } = {}) => {
    const name = (newProjectName || '').trim();
    if (!name) {
      toast.error('Project name is required');
      return null;
    }
    setCreatingProject(true);
    try {
      // Minimal ProjectCreate: name + project_type + service_line are the
      // only required fields. Inherit the contract's service category as the
      // service line, use the methodology the user picked, and carry the
      // account so the project belongs to the same client.
      // Project service_line uses a different (smaller) vocabulary than the
      // contract's service_category. Only inherit it when it's a valid
      // service_line; otherwise default to 'other' so the create never 422s.
      const VALID_SERVICE_LINES = ['ucaas', 'networking', 'cloud', 'security', 'ai_product', 'other'];
      const serviceLine = VALID_SERVICE_LINES.includes(newContractForm.service_category)
        ? newContractForm.service_category
        : 'other';

      // Shape methodology_config to match the chosen project_type. The backend
      // validator 422s on a mismatch, so fail here with a readable message
      // rather than surfacing a validation error from the API.
      const { config: methodologyConfig, error: methodologyError } = buildMethodologyConfig(
        newProjectType,
        methodologyFields,
      );
      if (methodologyError) {
        toast.error(methodologyError);
        setCreatingProject(false);
        return null;
      }

      const res = await api.post(`${API}/api/projects/projects`, {
        name,
        project_type: newProjectType,
        service_line: serviceLine,
        account_id: newContractForm.account_id || undefined,
        methodology_config: methodologyConfig,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.detail || 'Failed to create project');
        return null;
      }
      const created = data?.item || data?.project || data;
      const newId = created.project_id || created.id;
      if (!newId) {
        toast.error('Project created but no id was returned');
        return null;
      }
      // Add to the picker list and select it, then collapse the inline form.
      setAssignProjects((prev) => [created, ...prev]);
      setNewContractForm((prev) => ({ ...prev, project_ids: [newId] }));
      setShowNewProject(false);
      setNewProjectName('');
      if (!silent) toast.success(`Project "${name}" created and linked`);
      return newId;
    } catch (e) {
      toast.error('Failed to create project: ' + (e.message || 'Unknown error'));
      return null;
    } finally {
      setCreatingProject(false);
    }
  };

  const handleAssignContract = async () => {
    if (!assignContractDeal) return;

    setAssignLoading(true);
    try {
      if (assignMode === 'existing') {
        if (!assignForm.contract_id) {
          toast.error('Please select a contract');
          setAssignLoading(false);
          return;
        }

        // Attach to existing contract
        const res = await api.post(`${API}/api/contracts/import-deal`, {
          deal_id: assignContractDeal.deal_id || assignContractDeal.zoho_deal_id,
          contract_id: assignForm.contract_id,
        });

        if (res.ok) {
          toast.success('Deal attached to contract');
          setAssignContractDeal(null);
          setAssignForm({ contract_id: '' });
          loadDeals();
        } else {
          const data = await res.json();
          toast.error(data.detail || 'Failed to attach deal');
        }
      } else {
        // Create new contract
        if (!newContractForm.name || !newContractForm.service_category) {
          toast.error('Contract name and service category are required');
          setAssignLoading(false);
          return;
        }

        // Guard: no resource may be assigned at 0% allocation. A 0% row makes
        // the contract's financial summary read $0 cost / 100% margin — a
        // number that gets believed (Ryan approved blocking it). Empty rows
        // (no contractor selected) are ignored; only a chosen contractor at
        // 0% is rejected.
        const zeroAlloc = (newContractForm.resources || []).filter(
          (r) => r.user_id && (parseFloat(r.allocation_percentage) || 0) <= 0,
        );
        if (zeroAlloc.length > 0) {
          toast.error(
            'Every assigned contractor needs an allocation above 0%. ' +
            'A 0% row would show the contract as costing nothing.',
          );
          setAssignLoading(false);
          return;
        }

        // Resolve the linked project. If the user typed a new-project name in
        // the inline form but didn't press "Create & link", create it now so
        // "Create & Attach" just works (state updates are async, so we use the
        // returned id directly rather than reading project_ids back).
        let effectiveProjectIds = newContractForm.project_ids || [];
        if (effectiveProjectIds.length === 0 && showNewProject && newProjectName.trim()) {
          const newId = await handleCreateInlineProject({ silent: true });
          if (!newId) {
            setAssignLoading(false);
            return; // creation failed; toast already shown
          }
          effectiveProjectIds = [newId];
        }
        if (effectiveProjectIds.length === 0) {
          toast.error('A linked project is required');
          setAssignLoading(false);
          return;
        }

        // Build contract payload
        const contractPayload = {
          name: newContractForm.name,
          account_id: assignContractDeal.account_id,
          service_category: newContractForm.service_category,
          contract_type: newContractForm.contract_type,
          start_date: newContractForm.start_date,
          end_date: newContractForm.end_date || null,
          deal_ids: [assignContractDeal.deal_id || assignContractDeal.zoho_deal_id],
          // Linked project(s) — required by the backend for deal-linked contracts.
          project_ids: effectiveProjectIds,
          // Resources/types are collected by ContractFormFields; send them so the
          // contract isn't created empty (previously dropped here).
          resource_types: newContractForm.resource_types || [],
          resources: newContractForm.resources || [],
          notes: newContractForm.notes || '',
        };
        // status is intentionally omitted — the backend creates every contract
        // as pending; it is never auto-approved.

        // Add type-specific fields
        if (newContractForm.contract_type === 'boh') {
          contractPayload.total_hours = parseFloat(newContractForm.total_hours) || 0;
          contractPayload.hourly_rate = parseFloat(newContractForm.hourly_rate) || 0;
        } else if (newContractForm.contract_type === 'boa') {
          contractPayload.billing_period = newContractForm.billing_period;
          contractPayload.invoice_terms = newContractForm.invoice_terms;
        } else if (newContractForm.contract_type === 'monthly') {
          contractPayload.monthly_rate = parseFloat(newContractForm.monthly_rate) || 0;
          contractPayload.billing_day = parseInt(newContractForm.billing_day) || 1;
        } else if (newContractForm.contract_type === 'fixed_fee') {
          contractPayload.total_amount = parseFloat(newContractForm.total_amount) || 0;
          contractPayload.milestones = newContractForm.milestones || [];
        }

        const res = await api.post(`${API}/api/contracts`, contractPayload);

        if (res.ok) {
          toast.success('Contract created — pending approval');
          setAssignContractDeal(null);
          setAssignForm({ contract_id: '' });
          setNewContractForm({
            name: '',
            contract_type: 'boh',
            start_date: new Date().toISOString().split('T')[0],
            total_hours: '',
            hourly_rate: '',
            monthly_rate: '',
            total_amount: '',
            notes: '',
          });
          loadDeals();
        } else {
          const data = await res.json();
          toast.error(data.detail || 'Failed to create contract');
        }
      }
    } catch (err) {
      toast.error(err.message || 'Failed to process request');
    } finally {
      setAssignLoading(false);
    }
  };

  // Debounce the search box so filtering doesn't run on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Apply advanced filters (search, account, owner, etc.)
  const baseFilteredDeals = deals.filter((deal) => {
    // Search (debounced)
    if (debouncedSearch) {
      const search = debouncedSearch.toLowerCase();
      const matchName = (deal.name || deal.deal_name || '').toLowerCase().includes(search);
      const matchAccount = (deal.account_name || '').toLowerCase().includes(search);
      if (!matchName && !matchAccount) return false;
    }

    // Account filter
    if (filters.account_id && deal.account_id !== filters.account_id) return false;

    // Deal owner filter
    if (filters.deal_owner_id && deal.deal_owner_id !== filters.deal_owner_id) return false;

    // Agent of record filter
    if (filters.agent_of_record_id && deal.assigned_agent_id !== filters.agent_of_record_id)
      return false;

    // Contract status
    if (
      filters.contract_status === 'with_contract' &&
      (!deal.contracts || deal.contracts.length === 0)
    )
      return false;
    if (filters.contract_status === 'no_contract' && deal.contracts && deal.contracts.length > 0)
      return false;

    // Commission status
    if (
      filters.commission_status === 'with_commission' &&
      !deal.commission_id &&
      !deal.commission_plan_id
    )
      return false;
    if (
      filters.commission_status === 'no_commission' &&
      (deal.commission_id || deal.commission_plan_id)
    )
      return false;

    return true;
  });

  // Statistics — always computed from base filter (unaffected by quick filter)
  const stats = {
    total: baseFilteredDeals.length,
    needsContract: baseFilteredDeals.filter((d) => !d.contracts || d.contracts.length === 0).length,
    needsCommission: baseFilteredDeals.filter((d) => !d.commission_id && !d.commission_plan_id)
      .length,
    needsBoth: baseFilteredDeals.filter(
      (d) =>
        (!d.contracts || d.contracts.length === 0) && !d.commission_id && !d.commission_plan_id,
    ).length,
    complete: baseFilteredDeals.filter(
      (d) => d.contracts && d.contracts.length > 0 && (d.commission_id || d.commission_plan_id),
    ).length,
  };

  // Apply quick filter on top of base
  const filteredDeals = baseFilteredDeals.filter((deal) => {
    const hasContract = deal.contracts && deal.contracts.length > 0;
    const hasCommission = deal.commission_id || deal.commission_plan_id;

    switch (quickFilter) {
      case 'needs_both':
        return !hasContract && !hasCommission;
      case 'needs_contract':
        return !hasContract;
      case 'needs_commission':
        return !hasCommission;
      case 'complete':
        return hasContract && hasCommission;
      default: // 'all' - show ALL deals regardless of status
        return true;
    }
  });

  // Reset to the first page whenever the result set changes (search, quick
  // filter, advanced filters, or page size), so we never land on an empty page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, quickFilter, filters, pageSize]);

  // The slice of deals visible on the current page.
  const totalFiltered = filteredDeals.length;
  const pagedDeals = filteredDeals.slice((page - 1) * pageSize, page * pageSize);

  // Filter dropdown options derived from the loaded deals themselves — the
  // options mirror the data (every actual owner/agent), so no role guess is
  // needed and a stale/dead role string can never empty the list. Keyed on the
  // same fields the filter matches (deal_owner_id / assigned_agent_id) so a
  // selected value always lines up with a real deal.
  const dealOwnerOptions = useMemo(() => {
    const seen = new Map(); // id -> name; Map key dedupes
    deals.forEach((d) => {
      if (d.deal_owner_id) seen.set(d.deal_owner_id, d.deal_owner_name || 'Unknown');
    });
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [deals]);

  const agentOfRecordOptions = useMemo(() => {
    const seen = new Map();
    deals.forEach((d) => {
      // ID field on a deal is `assigned_agent_id`; display name is
      // `agent_of_record_name` (verified different prefixes).
      if (d.assigned_agent_id) seen.set(d.assigned_agent_id, d.agent_of_record_name || 'Unknown');
    });
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [deals]);

  const getDealStatus = (deal) => {
    const hasContract = deal.contracts && deal.contracts.length > 0;
    const hasCommission = deal.commission_id || deal.commission_plan_id;

    if (hasContract && hasCommission)
      return {
        label: 'Complete',
        color: 'text-emerald-600 dark:text-emerald-400',
        icon: CheckCircle,
      };
    if (!hasContract && !hasCommission)
      return { label: 'Needs Both', color: 'text-red-600 dark:text-red-400', icon: AlertCircle };
    if (!hasContract)
      return {
        label: 'Needs Contract',
        color: 'text-amber-600 dark:text-amber-400',
        icon: FileText,
      };
    if (!hasCommission)
      return {
        label: 'Needs Commission',
        color: 'text-violet-600 dark:text-violet-400',
        icon: DollarSign,
      };
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-boss-text flex items-center gap-2">
            <Target size={20} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
            Deal Management
          </h1>
          <p className="text-xs sm:text-sm text-boss-text-secondary mt-1">
            Closed Won deals requiring contract and commission assignment
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin() && (
            <div className="flex items-center gap-2 text-xs">
              <Filter size={14} className="text-boss-text-muted shrink-0" />
              <select
                value={dataFilter}
                onChange={(e) => setDataFilter(e.target.value)}
                className="px-3 py-2 bg-boss-elevated border border-boss-border rounded-lg text-boss-text-secondary font-medium focus:border-boss-border-hover focus:outline-none cursor-pointer hover:border-boss-border-hover transition"
              >
                <option value="all">All Data</option>
                <option value="test">Test Data</option>
                <option value="real">Real Data</option>
              </select>
            </div>
          )}
          {canSync && (
            <button
              onClick={handleSyncDeals}
              disabled={syncing}
              className={`flex items-center gap-2 text-sm px-3 sm:px-4 py-2 rounded-lg transition border ${
                syncing
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700/30 text-emerald-600 dark:text-emerald-400'
                  : 'bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 border-emerald-200 dark:border-emerald-700/40 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300'
              }`}
            >
              <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">
                {syncing ? 'Syncing Deals...' : 'Sync Deals'}
              </span>
              <span className="sm:hidden">{syncing ? 'Syncing...' : 'Sync'}</span>
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
            className="flex items-center gap-2 bg-boss-elevated hover:bg-boss-surface text-boss-text text-sm px-3 sm:px-4 py-2 rounded-lg transition border border-boss-border"
          >
            <Filter size={16} />
            Filters {showFilters && <X size={14} />}
          </button>
        </div>
      </div>

      {/* Statistics — Clickable Quick Filters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
        <button
          onClick={() => setQuickFilter('all')}
          aria-pressed={quickFilter === 'all'}
          className={`text-left rounded-lg p-2.5 sm:p-3 transition border ${
            quickFilter === 'all'
              ? 'bg-boss-surface border-violet-300 dark:border-white/30 ring-1 ring-violet-200 dark:ring-white/20 shadow-sm'
              : 'bg-boss-surface border-boss-border hover:border-boss-border-hover shadow-sm'
          }`}
        >
          <div className="text-xl sm:text-2xl font-bold text-boss-text">{stats.total}</div>
          <div className="text-[11px] sm:text-xs text-boss-text-secondary mt-1">Total Deals</div>
        </button>
        <button
          onClick={() => setQuickFilter(quickFilter === 'needs_both' ? 'all' : 'needs_both')}
          aria-pressed={quickFilter === 'needs_both'}
          className={`text-left rounded-lg p-2.5 sm:p-3 transition border ${
            quickFilter === 'needs_both'
              ? 'bg-red-100 dark:bg-red-900/40 border-red-500/50 ring-1 ring-red-500/30'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30 hover:border-red-600/50'
          }`}
        >
          <div className="text-xl sm:text-2xl font-bold text-red-600 dark:text-red-400">
            {stats.needsBoth}
          </div>
          <div className="text-[11px] sm:text-xs text-red-700 dark:text-red-300 mt-1">
            Needs Both
          </div>
        </button>
        <button
          onClick={() =>
            setQuickFilter(quickFilter === 'needs_contract' ? 'all' : 'needs_contract')
          }
          aria-pressed={quickFilter === 'needs_contract'}
          className={`text-left rounded-lg p-2.5 sm:p-3 transition border ${
            quickFilter === 'needs_contract'
              ? 'bg-amber-100 dark:bg-amber-900/40 border-amber-500/50 ring-1 ring-amber-500/30'
              : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30 hover:border-amber-600/50'
          }`}
        >
          <div className="text-xl sm:text-2xl font-bold text-amber-600 dark:text-amber-400">
            {stats.needsContract}
          </div>
          <div className="text-[11px] sm:text-xs text-amber-700 dark:text-amber-300 mt-1">
            Needs Contract
          </div>
        </button>
        <button
          onClick={() =>
            setQuickFilter(quickFilter === 'needs_commission' ? 'all' : 'needs_commission')
          }
          aria-pressed={quickFilter === 'needs_commission'}
          className={`text-left rounded-lg p-2.5 sm:p-3 transition border ${
            quickFilter === 'needs_commission'
              ? 'bg-violet-100 dark:bg-violet-900/40 border-violet-500/50 ring-1 ring-violet-500/30'
              : 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800/30 hover:border-violet-600/50'
          }`}
        >
          <div className="text-xl sm:text-2xl font-bold text-violet-600 dark:text-violet-400">
            {stats.needsCommission}
          </div>
          <div className="text-[11px] sm:text-xs text-violet-700 dark:text-violet-300 mt-1">
            Needs Commission
          </div>
        </button>
        <button
          onClick={() => setQuickFilter(quickFilter === 'complete' ? 'all' : 'complete')}
          aria-pressed={quickFilter === 'complete'}
          className={`col-span-2 sm:col-span-1 text-left rounded-lg p-2.5 sm:p-3 transition border ${
            quickFilter === 'complete'
              ? 'bg-emerald-100 dark:bg-emerald-900/40 border-emerald-500/50 ring-1 ring-emerald-500/30'
              : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/30 hover:border-emerald-600/50'
          }`}
        >
          <div className="text-xl sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            {stats.complete}
          </div>
          <div className="text-[11px] sm:text-xs text-emerald-700 dark:text-emerald-300 mt-1">
            Complete
          </div>
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mt-4 bg-boss-surface border border-boss-border rounded-lg p-3 sm:p-4 space-y-3 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Search */}
            <div>
              <label className="text-xs text-boss-text-secondary block mb-1">Search</label>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-2.5 text-boss-text-muted pointer-events-none"
                />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Deal or account name..."
                  className="w-full pl-9 pr-3 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text outline-none focus:border-violet-500"
                />
              </div>
            </div>

            {/* Account */}
            <div>
              <label className="text-xs text-boss-text-secondary block mb-1">Account</label>
              <select
                value={filters.account_id}
                onChange={(e) => setFilters((f) => ({ ...f, account_id: e.target.value }))}
                className="w-full px-3 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text outline-none focus:border-violet-500"
              >
                <option value="">All Accounts</option>
                {accounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Deal Owner */}
            <div>
              <label className="text-xs text-boss-text-secondary block mb-1">Deal Owner</label>
              <select
                value={filters.deal_owner_id}
                onChange={(e) => setFilters((f) => ({ ...f, deal_owner_id: e.target.value }))}
                className="w-full px-3 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text outline-none focus:border-violet-500"
              >
                <option value="">All Owners</option>
                {dealOwnerOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Agent of Record */}
            <div>
              <label className="text-xs text-boss-text-secondary block mb-1">Agent of Record</label>
              <select
                value={filters.agent_of_record_id}
                onChange={(e) => setFilters((f) => ({ ...f, agent_of_record_id: e.target.value }))}
                className="w-full px-3 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text outline-none focus:border-violet-500"
              >
                <option value="">All Agents</option>
                {agentOfRecordOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Contract Status */}
            <div>
              <label className="text-xs text-boss-text-secondary block mb-1">Contract Status</label>
              <select
                value={filters.contract_status}
                onChange={(e) => setFilters((f) => ({ ...f, contract_status: e.target.value }))}
                className="w-full px-3 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text outline-none focus:border-violet-500"
              >
                <option value="all">All</option>
                <option value="no_contract">No Contract</option>
                <option value="with_contract">With Contract</option>
              </select>
            </div>

            {/* Commission Status */}
            <div>
              <label className="text-xs text-boss-text-secondary block mb-1">
                Commission Status
              </label>
              <select
                value={filters.commission_status}
                onChange={(e) => setFilters((f) => ({ ...f, commission_status: e.target.value }))}
                className="w-full px-3 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text outline-none focus:border-violet-500"
              >
                <option value="all">All</option>
                <option value="no_commission">No Commission</option>
                <option value="with_commission">With Commission</option>
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setFilters({
                search: '',
                account_id: '',
                deal_owner_id: '',
                agent_of_record_id: '',
                contract_status: 'all',
                commission_status: 'all',
              });
              setSearchInput('');
              setQuickFilter('all');
            }}
            className="text-xs text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 py-1"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Active Quick Filter Indicator + Deal Count */}
      {quickFilter !== 'all' && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-boss-text-secondary">Showing:</span>
          <span
            className={`px-2 py-0.5 rounded-full font-medium ${
              quickFilter === 'needs_both'
                ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/30'
                : quickFilter === 'needs_contract'
                  ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/30'
                  : quickFilter === 'needs_commission'
                    ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-800/30'
                    : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/30'
            }`}
          >
            {quickFilter === 'needs_both'
              ? 'Needs Both'
              : quickFilter === 'needs_contract'
                ? 'Needs Contract'
                : quickFilter === 'needs_commission'
                  ? 'Needs Commission'
                  : 'Complete'}{' '}
            ({filteredDeals.length})
          </span>
          <button
            onClick={() => setQuickFilter('all')}
            aria-label="Clear filter"
            className="text-boss-text-muted hover:text-boss-text transition p-1"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="mb-2 relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-boss-text-muted pointer-events-none"
        />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search deals by name or account…"
          aria-label="Search deals"
          className="w-full pl-9 pr-9 py-2.5 bg-boss-surface border border-boss-border rounded-lg text-sm text-boss-text outline-none focus:border-violet-500"
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput('')}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-boss-text-muted hover:text-boss-text transition"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Deals List */}
      <div className="space-y-2">
        {loading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-boss-surface border border-boss-border rounded-lg p-4 animate-pulse"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-40 bg-boss-elevated rounded" />
                      <div className="h-3 w-16 bg-boss-elevated rounded" />
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      {[0, 1, 2, 3].map((j) => (
                        <div key={j} className="space-y-1.5">
                          <div className="h-3 w-16 bg-boss-elevated rounded" />
                          <div className="h-3 w-24 bg-boss-elevated rounded" />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 sm:ml-4">
                    <div className="h-7 w-28 bg-boss-elevated rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredDeals.length === 0 && deals.length === 0 && (
          <div className="bg-boss-surface border border-boss-border rounded-lg p-6 sm:p-8 text-center">
            <Target size={48} className="mx-auto text-boss-text-muted/60 mb-3" />
            <p className="text-boss-text font-medium mb-2">No Closed Won Deals Found</p>
            <p className="text-boss-text-secondary text-sm mb-4">
              Get started by syncing deals from Zoho CRM or checking your deal stage filters.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-center gap-2 sm:gap-3">
              <button
                onClick={() => (window.location.href = '/admin/integrations')}
                className="bg-blue-600 hover:bg-blue-500 text-white text-sm px-4 py-2 rounded-lg transition"
              >
                Configure Zoho Sync
              </button>
              <button
                onClick={() => loadDeals()}
                className="bg-boss-elevated hover:bg-boss-surface text-boss-text text-sm px-4 py-2 rounded-lg transition"
              >
                Refresh Data
              </button>
            </div>
          </div>
        )}

        {!loading && filteredDeals.length === 0 && deals.length > 0 && (
          <div className="bg-boss-surface border border-boss-border rounded-lg p-6 sm:p-8 text-center">
            <Filter size={48} className="mx-auto text-boss-text-muted/60 mb-3" />
            <p className="text-boss-text font-medium mb-2">No deals match your filters</p>
            <p className="text-boss-text-secondary text-sm mb-4">
              Try adjusting your filters or clearing them to see all deals.
            </p>
            <button
              onClick={() => {
                setFilters({
                  search: '',
                  account_id: '',
                  deal_owner_id: '',
                  agent_of_record_id: '',
                  contract_status: 'all',
                  commission_status: 'all',
                });
                setSearchInput('');
                setQuickFilter('all');
              }}
              className="bg-violet-600 hover:bg-violet-500 text-white text-sm px-4 py-2 rounded-lg transition"
            >
              Clear All Filters
            </button>
          </div>
        )}

        {!loading &&
          pagedDeals.map((deal) => {
            const status = getDealStatus(deal);
            const StatusIcon = status.icon;
            const hasContract = deal.contracts && deal.contracts.length > 0;
            const hasCommission = deal.commission_id || deal.commission_plan_id;
            const isExpanded = selectedDeal?.deal_id === deal.deal_id;
            const notes = deal.zoho_notes || [];
            const attachments = deal.zoho_attachments || [];
            const pandaDocs = deal.pandadoc_documents || [];

            return (
              <div
                key={deal.deal_id || deal.zoho_deal_id}
                className="bg-boss-surface border border-boss-border rounded-lg hover:border-boss-border-hover transition"
              >
                {/* Clickable Header Row */}
                <div
                  className="p-3 sm:p-4 cursor-pointer"
                  onClick={() => setSelectedDeal(isExpanded ? null : deal)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    {/* Deal Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="text-sm sm:text-base font-semibold text-boss-text">
                          {deal.name || deal.deal_name}
                        </h3>
                        <span className={`flex items-center gap-1 text-xs ${status.color}`}>
                          <StatusIcon size={12} />
                          {status.label}
                        </span>
                        {/* Enrichment badges */}
                        {notes.length > 0 && (
                          <span className="flex items-center gap-0.5 text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/30 px-1.5 py-0.5 rounded-full">
                            <MessageSquare size={10} /> {notes.length}
                          </span>
                        )}
                        {attachments.length > 0 && (
                          <span className="flex items-center gap-0.5 text-[10px] bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/30 px-1.5 py-0.5 rounded-full">
                            <Paperclip size={10} /> {attachments.length}
                          </span>
                        )}
                        {pandaDocs.length > 0 && (
                          <span className="flex items-center gap-0.5 text-[10px] bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800/30 px-1.5 py-0.5 rounded-full">
                            <ExternalLink size={10} /> PandaDoc
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-xs">
                        {/* Column 1: Basic Info */}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1 text-boss-text-muted mb-1">
                            <Building2 size={12} className="shrink-0" />
                            <span>Account</span>
                          </div>
                          <div
                            className="text-boss-text font-medium cursor-pointer hover:text-amber-600 dark:hover:text-amber-400 transition truncate"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (deal.account_id) {
                                navigate(`/accounts?id=${deal.account_id}`);
                              }
                            }}
                          >
                            {deal.account_name}
                          </div>
                          <div className="text-boss-text-secondary mt-1">
                            {fmt(deal.amount || 0)}
                          </div>
                        </div>

                        {/* Column 2: People */}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1 text-boss-text-muted mb-1">
                            <Users size={12} className="shrink-0" />
                            <span>Team</span>
                          </div>
                          <div className="text-boss-text truncate">
                            {deal.deal_owner_name || 'No owner'}
                          </div>
                          {deal.agent_of_record_name && (
                            <div className="text-boss-text-secondary text-[10px] mt-0.5 truncate">
                              AoR: {deal.agent_of_record_name}
                            </div>
                          )}
                        </div>

                        {/* Column 3: Contract */}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1 text-boss-text-muted mb-1">
                            <FileText size={12} className="shrink-0" />
                            <span>Contract</span>
                          </div>
                          {hasContract ? (
                            <div className="space-y-0.5">
                              {deal.contracts.slice(0, 2).map((c, i) => (
                                <div
                                  key={i}
                                  className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium text-[11px] cursor-pointer hover:text-emerald-700 dark:hover:text-emerald-300 transition truncate"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (c.contract_id) {
                                      navigate(`/contracts?id=${c.contract_id}`);
                                    }
                                  }}
                                >
                                  <CheckCircle size={11} className="shrink-0" />
                                  <span className="truncate">
                                    {c.name} • {c.contract_type.toUpperCase()}
                                  </span>
                                </div>
                              ))}
                              {deal.contracts.length > 2 && (
                                <div className="text-boss-text-muted text-[10px]">
                                  +{deal.contracts.length - 2} more
                                </div>
                              )}
                            </div>
                          ) : deal.deal_types?.includes('referral_mrr') ? (
                            <div className="text-boss-text-muted italic text-xs">
                              N/A (Referral)
                            </div>
                          ) : (
                            <div
                              className="flex items-center gap-1 text-amber-600 dark:text-amber-400 cursor-pointer hover:text-amber-700 dark:hover:text-amber-300 transition"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canEdit) {
                                  setAssignContractDeal(deal);
                                  setAssignMode('new');
                                  setAssignForm({ contract_id: '' });
                                }
                              }}
                            >
                              <AlertCircle size={11} className="shrink-0" />
                              Not assigned
                            </div>
                          )}
                        </div>

                        {/* Column 4: Commission */}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1 text-boss-text-muted mb-1">
                            <DollarSign size={12} className="shrink-0" />
                            <span>Commission</span>
                          </div>
                          {hasCommission ? (
                            <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                              <CheckCircle size={11} className="shrink-0" />
                              Plan committed
                            </div>
                          ) : (
                            <div
                              className="flex items-center gap-1 text-violet-600 dark:text-violet-400 cursor-pointer hover:text-violet-700 dark:hover:text-violet-300 transition"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canEdit) {
                                  openCommissionModal(deal);
                                }
                              }}
                            >
                              <AlertCircle size={11} className="shrink-0" />
                              Not committed
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expand/Actions Column */}
                    <div
                      className="flex flex-wrap items-center gap-2 sm:ml-4 sm:shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {canEdit && !hasContract && !deal.deal_types?.includes('referral_mrr') && (
                        <button
                          onClick={() => {
                            setAssignContractDeal(deal);
                            setAssignMode('new');
                            setAssignForm({ contract_id: '' });
                          }}
                          className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs px-3 py-1.5 rounded-md transition whitespace-nowrap"
                        >
                          <FileText size={14} /> Assign Contract
                        </button>
                      )}
                      {canEdit && !hasCommission && (
                        <button
                          onClick={() => openCommissionModal(deal)}
                          className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs px-3 py-1.5 rounded-md transition whitespace-nowrap"
                        >
                          <DollarSign size={14} /> Commit Commission
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label={isExpanded ? 'Collapse deal' : 'Expand deal'}
                        onClick={() => setSelectedDeal(isExpanded ? null : deal)}
                        className="flex items-center justify-center ml-auto sm:ml-0 w-8 h-8 rounded-md text-boss-text-muted hover:text-boss-text hover:bg-boss-elevated transition"
                      >
                        <ChevronDown
                          size={18}
                          className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* ========= EXPANDED DETAIL PANEL ========= */}
                {isExpanded && (
                  <div className="border-t border-boss-border p-3 sm:p-4 space-y-4 bg-boss-base/50">
                    {/* Row 1: Deal Details + Account Info */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                      {/* Deal Details */}
                      <div className="bg-boss-accent-muted rounded-lg p-3">
                        <h4 className="text-xs font-semibold text-boss-text-secondary mb-2 flex items-center gap-1.5">
                          <Target size={14} className="text-emerald-600 dark:text-emerald-400" />{' '}
                          Deal Details
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                          <div>
                            <span className="text-boss-text-muted">Deal ID:</span>
                            <span className="text-boss-text-secondary ml-1 font-mono text-[10px]">
                              {deal.zoho_deal_id || deal.deal_id}
                            </span>
                          </div>
                          <div>
                            <span className="text-boss-text-muted">Amount:</span>
                            <span className="text-boss-text ml-1 font-semibold">
                              {fmt(deal.amount || 0)}
                            </span>
                          </div>
                          <div>
                            <span className="text-boss-text-muted">Close Date:</span>
                            <span className="text-boss-text-secondary ml-1">
                              {deal.close_date || '—'}
                            </span>
                          </div>
                          <div>
                            <span className="text-boss-text-muted">Stage:</span>
                            <span className="text-emerald-600 dark:text-emerald-400 ml-1">
                              {deal.stage}
                            </span>
                          </div>
                          <div>
                            <span className="text-boss-text-muted">Owner:</span>
                            <span className="text-boss-text ml-1">
                              {deal.deal_owner_name || '—'}
                            </span>
                            {deal.deal_owner_email && (
                              <span className="text-boss-text-muted ml-1 text-[10px]">
                                ({deal.deal_owner_email})
                              </span>
                            )}
                          </div>
                          <div>
                            <span className="text-boss-text-muted">Agent of Record:</span>
                            <span className="text-boss-text ml-1">
                              {deal.agent_of_record_name || '—'}
                            </span>
                          </div>
                          {deal.deal_types && deal.deal_types.length > 0 && (
                            <div className="col-span-2">
                              <span className="text-boss-text-muted">Line of Business:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {deal.deal_types.map((type, idx) => {
                                  const typeLabels = {
                                    field_services: {
                                      label: 'Field Services',
                                      color:
                                        'bg-blue-50 dark:bg-blue-600/20 text-blue-700 dark:text-blue-300 border-blue-500/30',
                                    },
                                    professional_services: {
                                      label: 'Professional Services',
                                      color:
                                        'bg-purple-600/20 text-purple-300 border-purple-500/30',
                                    },
                                    referral_mrr: {
                                      label: 'Referral MRR',
                                      color:
                                        'bg-pink-50 dark:bg-pink-600/20 text-pink-700 dark:text-pink-300 border-pink-500/30',
                                    },
                                    wholesale_mrr: {
                                      label: 'Wholesale MRR',
                                      color:
                                        'bg-cyan-600/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
                                    },
                                  };
                                  const config = typeLabels[type] || {
                                    label: type,
                                    color:
                                      'bg-boss-elevated/20 text-boss-text-secondary border-boss-border-hover/30',
                                  };
                                  return (
                                    <span
                                      key={idx}
                                      className={`px-2 py-0.5 rounded text-[10px] font-medium border ${config.color}`}
                                    >
                                      {config.label}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        {deal.description && (
                          <div className="mt-2 pt-2 border-t border-boss-border">
                            <span className="text-boss-text-muted text-[10px]">Description:</span>
                            <p className="text-boss-text-secondary text-xs mt-0.5 leading-relaxed">
                              {deal.description}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Contract + Commission Details */}
                      <div className="space-y-3">
                        {/* Contract Details */}
                        <div className="bg-boss-accent-muted rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-boss-text-secondary mb-2 flex items-center gap-1.5">
                            <FileText size={14} className="text-amber-600 dark:text-amber-400" />{' '}
                            Contracts
                          </h4>
                          {hasContract ? (
                            deal.contracts.map((contract, idx) => (
                              <div
                                key={idx}
                                className="bg-boss-surface rounded p-2 mb-1.5 last:mb-0 cursor-pointer hover:bg-boss-surface transition"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (contract.contract_id) {
                                    navigate(`/contracts?id=${contract.contract_id}`);
                                  }
                                }}
                              >
                                <div className="text-boss-text font-medium text-xs hover:text-amber-600 dark:text-amber-400 transition">
                                  {contract.name}
                                </div>
                                <div className="text-boss-text-secondary text-[10px] mt-0.5">
                                  Type: {contract.contract_type?.toUpperCase()}
                                  {contract.total_amount
                                    ? ` • Value: ${fmt(contract.total_amount)}`
                                    : ''}
                                  {contract.monthly_rate
                                    ? ` • ${fmt(contract.monthly_rate)}/month`
                                    : ''}
                                  {contract.status ? ` • Status: ${contract.status}` : ''}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div
                              className="text-amber-600 dark:text-amber-400 text-xs italic cursor-pointer hover:text-amber-700 dark:text-amber-300 transition"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canEdit) {
                                  setAssignContractDeal(deal);
                                  setAssignMode('new');
                                  setAssignForm({ contract_id: '' });
                                }
                              }}
                            >
                              No contract assigned
                            </div>
                          )}
                        </div>
                        {/* Commission Details */}
                        <div className="bg-boss-accent-muted rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-boss-text-secondary mb-2 flex items-center gap-1.5">
                            <DollarSign
                              size={14}
                              className="text-violet-600 dark:text-violet-400"
                            />{' '}
                            Commission
                          </h4>
                          {hasCommission ? (
                            <div className="text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                              ✓ Plan Active — {deal.commission_id || deal.commission_plan_id}
                            </div>
                          ) : (
                            <div
                              className="text-violet-600 dark:text-violet-400 text-xs italic cursor-pointer hover:text-violet-700 dark:text-violet-300 transition"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canEdit) {
                                  openCommissionModal(deal);
                                }
                              }}
                            >
                              No commission plan committed
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Commission payouts (committed deals) */}
                    {hasCommission && (
                      <CommissionPayoutsPanel
                        commissionId={deal.commission_id || deal.commission_plan_id}
                        onChanged={loadDeals}
                        canManage={canEdit}
                      />
                    )}

                    {/* Row 2: Notes */}
                    {notes.length > 0 && (
                      <div className="bg-boss-accent-muted rounded-lg p-3">
                        <h4 className="text-xs font-semibold text-boss-text-secondary mb-2 flex items-center gap-1.5">
                          <MessageSquare size={14} className="text-blue-600 dark:text-blue-400" />{' '}
                          Notes ({notes.length})
                        </h4>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {notes.map((note, idx) => (
                            <div
                              key={idx}
                              className="bg-boss-surface rounded p-2 border-l-2 border-blue-800/50"
                            >
                              {note.title && (
                                <div className="text-boss-text font-medium text-xs mb-0.5">
                                  {note.title}
                                </div>
                              )}
                              <div
                                className="text-boss-text-secondary text-xs leading-relaxed"
                                dangerouslySetInnerHTML={{
                                  __html: note.content
                                    ? DOMPurify.sanitize(note.content)
                                    : '<em class="text-boss-text-muted">No content</em>',
                                }}
                              />
                              <div className="text-boss-text-muted text-[10px] mt-1">
                                {note.created_by && `By ${note.created_by}`}
                                {note.created_time &&
                                  ` • ${new Date(note.created_time).toLocaleDateString()}`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Row 3: Attachments + PandaDoc */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                      {/* Attachments */}
                      {attachments.length > 0 && (
                        <div className="bg-boss-accent-muted rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-boss-text-secondary mb-2 flex items-center gap-1.5">
                            <Paperclip size={14} className="text-amber-600 dark:text-amber-400" />{' '}
                            Attachments ({attachments.length})
                          </h4>
                          <div className="space-y-1.5 max-h-40 overflow-y-auto">
                            {attachments.map((att, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between bg-boss-surface rounded px-2 py-1.5"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <Paperclip
                                    size={12}
                                    className="text-boss-text-muted flex-shrink-0"
                                  />
                                  <span className="text-boss-text-secondary text-xs truncate">
                                    {att.file_name}
                                  </span>
                                </div>
                                <span className="text-boss-text-muted text-[10px] flex-shrink-0 ml-2">
                                  {att.size ? `${(att.size / 1024).toFixed(0)} KB` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* PandaDoc Documents */}
                      {pandaDocs.length > 0 && (
                        <div className="bg-boss-accent-muted rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-boss-text-secondary mb-2 flex items-center gap-1.5">
                            <ExternalLink
                              size={14}
                              className="text-green-600 dark:text-green-400"
                            />{' '}
                            PandaDoc Documents ({pandaDocs.length})
                          </h4>
                          <div className="space-y-1.5">
                            {pandaDocs.map((doc, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between bg-boss-surface rounded px-2 py-1.5"
                              >
                                <span className="text-boss-text-secondary text-xs truncate">
                                  {doc.name || 'Document'}
                                </span>
                                {doc.link && (
                                  <a
                                    href={doc.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 dark:text-green-400 hover:text-green-700 dark:text-green-300 text-[10px] flex items-center gap-0.5 flex-shrink-0 ml-2"
                                  >
                                    Open <ExternalLink size={10} />
                                  </a>
                                )}
                                {doc.status && (
                                  <span className="text-boss-text-muted text-[10px] ml-1">
                                    {doc.status}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Empty state for no enrichment */}
                      {attachments.length === 0 && pandaDocs.length === 0 && notes.length === 0 && (
                        <div className="col-span-2 text-center py-2 text-boss-text-muted text-xs italic">
                          No enrichment data (notes, attachments, or documents) from Zoho for this
                          deal.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* Pagination */}
      {!loading && totalFiltered > 0 && (
        <div className="mt-2">
          <PaginationControls
            page={page}
            pageSize={pageSize}
            total={totalFiltered}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testid="deals-pagination"
          />
        </div>
      )}

      {/* ============ ASSIGN CONTRACT MODAL ============ */}
      {assignContractDeal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Assign Contract"
        >
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setAssignContractDeal(null)}
          />
          <div className="relative bg-boss-surface border border-boss-border w-full h-full sm:rounded-xl sm:max-w-4xl sm:max-h-[90vh] sm:h-auto overflow-y-auto">
            <div className="sticky top-0 bg-boss-surface backdrop-blur border-b border-boss-border p-3 sm:p-4 z-10">
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-boss-text flex items-center gap-2">
                    <FileText size={16} className="text-amber-600 dark:text-amber-400 shrink-0" />
                    Assign Contract
                  </h2>
                  <p className="text-xs text-boss-text-secondary mt-1">
                    <span className="text-boss-text font-medium">
                      {assignContractDeal.name || assignContractDeal.deal_name}
                    </span>
                    {assignContractDeal.amount > 0 && (
                      <span className="ml-2">{fmt(assignContractDeal.amount)}</span>
                    )}
                    <span className="ml-2 text-boss-text-muted">
                      {assignContractDeal.account_name}
                    </span>
                  </p>
                </div>
                <button
                  onClick={() => setAssignContractDeal(null)}
                  aria-label="Close"
                  className="w-9 h-9 rounded-full flex items-center justify-center text-boss-text-muted hover:text-boss-text hover:bg-boss-elevated transition shrink-0"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="p-3 sm:p-4 space-y-4">
              {/* Mode Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setAssignMode('existing')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-xs transition ${
                    assignMode === 'existing'
                      ? 'bg-violet-50 dark:bg-violet-600/20 text-violet-700 dark:text-violet-300 border-violet-500/30'
                      : 'border-boss-border text-boss-text-secondary hover:border-boss-border-hover'
                  }`}
                >
                  Attach to Existing Contract
                </button>
                <button
                  onClick={() => setAssignMode('new')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-xs transition ${
                    assignMode === 'new'
                      ? 'bg-emerald-50 dark:bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                      : 'border-boss-border text-boss-text-secondary hover:border-boss-border-hover'
                  }`}
                >
                  Create New Contract
                </button>
              </div>

              {/* Existing Contract Mode */}
              {assignMode === 'existing' && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-boss-text-secondary block mb-1">
                      Select Contract *
                    </label>
                    <select
                      value={assignForm.contract_id}
                      onChange={(e) =>
                        setAssignForm((f) => ({ ...f, contract_id: e.target.value }))
                      }
                      className="w-full px-3 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text focus:border-violet-500 outline-none"
                    >
                      <option value="">Choose a contract...</option>
                      {contracts
                        .filter(
                          (c) =>
                            c.status === 'active' &&
                            (!assignContractDeal.account_id ||
                              c.account_id === assignContractDeal.account_id),
                        )
                        .map((c) => (
                          <option key={c.contract_id} value={c.contract_id}>
                            {c.name} ({c.contract_type?.toUpperCase()})
                            {c.account_name ? ` — ${c.account_name}` : ''}
                          </option>
                        ))}
                    </select>
                    {contracts.filter(
                      (c) =>
                        c.status === 'active' && c.account_id === assignContractDeal.account_id,
                    ).length === 0 && (
                      <p className="text-xs text-boss-text-muted mt-1">
                        No active contracts found for this account. Create a new one instead.
                      </p>
                    )}
                  </div>

                  <div className="bg-blue-900/15 border border-blue-700/30 rounded-lg p-3">
                    <p className="text-xs text-blue-700 dark:text-blue-300 font-medium mb-1">
                      Note:
                    </p>
                    <p className="text-xs text-boss-text-secondary">
                      This will link the deal to the selected contract. The contract's financials
                      and hours will be available for this deal.
                    </p>
                  </div>
                </div>
              )}

              {/* New Contract Mode */}
              {assignMode === 'new' && (
                <>
                  {draftLoading && (
                    <div className="flex items-center gap-2 text-xs text-boss-text-secondary bg-boss-accent-muted border border-boss-border rounded-md px-3 py-2 mb-2">
                      <span className="inline-block w-3 h-3 border-2 border-boss-border border-t-transparent rounded-full animate-spin" />
                      Loading Sales Ops data for this deal…
                    </div>
                  )}
                  {!draftLoading && draftNotice && (
                    <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-600/15 border border-amber-300 dark:border-amber-600/30 rounded-md px-3 py-2 mb-2">
                      <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                      <span>{draftNotice}</span>
                    </div>
                  )}
                  <ContractFormFields
                    form={newContractForm}
                    setForm={setNewContractForm}
                    accounts={accounts}
                    users={users}
                    defaultResourceTypes={defaultResourceTypes}
                    showServiceCategory={true}
                    compact={false}
                    isAdmin={isAdmin()}
                  />

                  {/* Linked Project — required. A contract cannot be created
                      without at least one linked project (Ryan, 26 Jun). */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-medium text-boss-text">
                        Linked Project <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewProject((v) => !v);
                          if (!showNewProject && !newProjectName) {
                            // Pre-fill from the contract/deal name for convenience.
                            setNewProjectName(newContractForm.name || '');
                          }
                        }}
                        className="text-[11px] font-medium text-boss-primary hover:underline"
                      >
                        {showNewProject ? 'Cancel' : '+ New project'}
                      </button>
                    </div>

                    {showNewProject ? (
                      <div className="space-y-2 p-2 rounded-lg border border-boss-border bg-boss-elevated">
                        <input
                          type="text"
                          value={newProjectName}
                          onChange={(e) => setNewProjectName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !creatingProject) {
                              e.preventDefault();
                              handleCreateInlineProject();
                            }
                          }}
                          placeholder="New project name"
                          autoFocus
                          className="w-full px-3 py-2 bg-boss-surface text-boss-text text-xs rounded-lg border border-boss-border focus:outline-none"
                        />

                        {/* Methodology picker. The Projects module wizard offers
                            all three; this inline creator used to force
                            waterfall. */}
                        <div>
                          <label className="block text-[11px] font-medium text-boss-text-secondary mb-1">
                            Methodology
                          </label>
                          <div className="grid grid-cols-3 gap-1" data-testid="inline-methodology-picker">
                            {PROJECT_TYPES.map((pt) => (
                              <button
                                key={pt}
                                type="button"
                                onClick={() => setNewProjectType(pt)}
                                data-testid={`inline-methodology-${pt}`}
                                title={METHODOLOGY_HINTS[pt]}
                                className={`px-2 py-1.5 text-[11px] rounded-md border transition ${
                                  newProjectType === pt
                                    ? 'border-boss-primary text-boss-primary bg-boss-accent-soft font-medium'
                                    : 'border-boss-border text-boss-text-secondary hover:border-boss-primary/50'
                                }`}
                              >
                                {METHODOLOGY_LABELS[pt]}
                              </button>
                            ))}
                          </div>
                          <p className="text-[10px] text-boss-text-secondary mt-1">
                            {METHODOLOGY_HINTS[newProjectType]}
                          </p>
                        </div>

                        {newProjectType === 'waterfall' && (
                          <div>
                            <label className="block text-[11px] font-medium text-boss-text-secondary mb-1">
                              Phases <span className="font-normal">(comma separated)</span>
                            </label>
                            <input
                              type="text"
                              value={methodologyFields.phases}
                              onChange={(e) =>
                                setMethodologyFields((f) => ({ ...f, phases: e.target.value }))
                              }
                              placeholder="Discovery, Build, Handover"
                              data-testid="inline-waterfall-phases"
                              className="w-full px-3 py-2 bg-boss-surface text-boss-text text-xs rounded-lg border border-boss-border focus:outline-none"
                            />
                          </div>
                        )}

                        {newProjectType === 'agile' && (
                          <div>
                            <label className="block text-[11px] font-medium text-boss-text-secondary mb-1">
                              Board columns <span className="font-normal">(comma separated)</span>
                            </label>
                            <input
                              type="text"
                              value={methodologyFields.columns}
                              onChange={(e) =>
                                setMethodologyFields((f) => ({ ...f, columns: e.target.value }))
                              }
                              placeholder="To Do, In Progress, Done"
                              data-testid="inline-agile-columns"
                              className="w-full px-3 py-2 bg-boss-surface text-boss-text text-xs rounded-lg border border-boss-border focus:outline-none"
                            />
                            <p className="text-[10px] text-boss-text-secondary mt-1">
                              The last column counts as done.
                            </p>
                          </div>
                        )}

                        {newProjectType === 'waves_threads' && (
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-[11px] font-medium text-boss-text-secondary mb-1">
                                Waves
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={methodologyFields.waveCountTarget}
                                onChange={(e) =>
                                  setMethodologyFields((f) => ({
                                    ...f,
                                    waveCountTarget: e.target.value,
                                  }))
                                }
                                data-testid="inline-waves-count"
                                className="w-full px-3 py-2 bg-boss-surface text-boss-text text-xs rounded-lg border border-boss-border focus:outline-none"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-[11px] font-medium text-boss-text-secondary mb-1">
                                Modules <span className="font-normal">(comma separated)</span>
                              </label>
                              <input
                                type="text"
                                value={methodologyFields.modules}
                                onChange={(e) =>
                                  setMethodologyFields((f) => ({ ...f, modules: e.target.value }))
                                }
                                placeholder="Core, Reporting"
                                data-testid="inline-waves-modules"
                                className="w-full px-3 py-2 bg-boss-surface text-boss-text text-xs rounded-lg border border-boss-border focus:outline-none"
                              />
                            </div>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={handleCreateInlineProject}
                          disabled={creatingProject || !newProjectName.trim()}
                          data-testid="inline-create-project"
                          className="w-full px-3 py-2 bg-boss-primary text-white text-xs rounded-lg disabled:opacity-50 transition"
                        >
                          {creatingProject ? 'Creating…' : 'Create & link'}
                        </button>
                      </div>
                    ) : (
                      <select
                        value={(newContractForm.project_ids || [])[0] || ''}
                        onChange={(e) =>
                          setNewContractForm((prev) => ({
                            ...prev,
                            project_ids: e.target.value ? [e.target.value] : [],
                          }))
                        }
                        className="w-full px-3 py-2 bg-boss-elevated text-boss-text text-xs rounded-lg border border-boss-border focus:outline-none"
                      >
                        <option value="">— Select a project to link —</option>
                        {assignProjects.map((p) => (
                          <option
                            key={p.project_id || p.id}
                            value={p.project_id || p.id}
                          >
                            {p.name || p.project_name || (p.project_id || p.id)}
                          </option>
                        ))}
                      </select>
                    )}
                    {!showNewProject && (!newContractForm.project_ids ||
                      newContractForm.project_ids.length === 0) && (
                      <p className="text-[11px] text-amber-600 mt-1">
                        A linked project is required before the contract can be created.
                        Pick one or use <span className="font-medium">+ New project</span>.
                      </p>
                    )}
                    {!showNewProject && assignProjects.length === 0 && (
                      <p className="text-[11px] text-boss-text-secondary mt-1">
                        No projects yet — use <span className="font-medium">+ New project</span> to create one here.
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setAssignContractDeal(null)}
                  className="flex-1 px-3 py-2 bg-boss-elevated hover:bg-boss-surface text-boss-text shadow-sm text-xs rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignContract}
                  disabled={
                    assignLoading ||
                    (assignMode === 'existing' && !assignForm.contract_id) ||
                    (assignMode === 'new' &&
                      (!newContractForm.name ||
                        !newContractForm.service_category ||
                        (!(newContractForm.project_ids || []).length &&
                          !(showNewProject && newProjectName.trim()))))
                  }
                  className="flex-1 px-3 py-2 bg-amber-600 hover:bg-amber-500 text-white text-xs rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assignLoading
                    ? 'Processing...'
                    : assignMode === 'existing'
                      ? 'Attach to Contract'
                      : 'Create & Attach'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ COMMISSION MODAL ============ */}
      {commissionDeal &&
        (() => {
          // Calculate commission in real-time
          const revenue = parseFloat(commissionForm.revenue) || 0;
          const costValue = parseFloat(commissionForm.cost_value) || 0;
          const actualCost =
            commissionForm.cost_type === 'percentage' ? revenue * (costValue / 100) : costValue;

          const tier1 = commissionForm.tiers[0] || {};
          const tier2 = commissionForm.tiers[1] || {};
          const tier3 = commissionForm.tiers[2] || {};

          const tier1Commission = revenue * ((tier1.percentage || 0) / 100);
          const tier2Base = revenue - actualCost - tier1Commission;
          const tier2Commission = tier2Base * ((tier2.percentage || 0) / 100);
          const tier3Base = tier2Base - tier2Commission;
          const tier3Commission = tier3Base * ((tier3.percentage || 0) / 100);

          const totalCommission = tier1Commission + tier2Commission + tier3Commission;
          const profitAfterCommission = revenue - actualCost - totalCommission;
          const profitPercentage = revenue > 0 ? (profitAfterCommission / revenue) * 100 : 0;

          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center sm:p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Commit Commission Plan"
            >
              <div
                className="absolute inset-0 bg-black/60"
                onClick={() => setCommissionDeal(null)}
              />
              <div className="relative bg-boss-surface border border-boss-border w-full h-full sm:rounded-xl sm:max-w-3xl sm:max-h-[90vh] sm:h-auto overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-boss-surface backdrop-blur border-b border-boss-border p-3 sm:p-4 z-10">
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0">
                      <h2 className="text-sm font-semibold text-boss-text flex items-center gap-2">
                        <DollarSign
                          size={16}
                          className="text-violet-600 dark:text-violet-400 shrink-0"
                        />
                        Commit Commission Plan
                      </h2>
                      <p className="text-xs text-boss-text-secondary mt-1">
                        <span className="text-boss-text font-medium">
                          {commissionDeal.name || commissionDeal.deal_name}
                        </span>
                        {commissionDeal.amount > 0 && (
                          <span className="ml-2">{fmt(commissionDeal.amount)}</span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => setCommissionDeal(null)}
                      aria-label="Close"
                      className="w-9 h-9 rounded-full flex items-center justify-center text-boss-text-muted hover:text-boss-text hover:bg-boss-elevated transition shrink-0"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>

                <div className="p-3 sm:p-4 space-y-4">
                  {/* Template Selection */}
                  <div>
                    <label className="text-xs text-boss-text-secondary block mb-1">
                      Load Template (optional)
                    </label>
                    <select
                      value={commissionForm.template_id}
                      onChange={(e) => {
                        const template = commissionTemplates.find(
                          (t) => t.template_id === e.target.value,
                        );
                        if (template) {
                          setCommissionForm((f) => ({
                            ...f,
                            template_id: template.template_id,
                            cost_type: template.cost_type,
                            cost_value: template.cost_default,
                            tiers: [
                              { ...f.tiers[0], percentage: template.tier1_percentage },
                              { ...f.tiers[1], percentage: template.tier2_percentage },
                              { ...f.tiers[2], percentage: template.tier3_percentage },
                            ],
                          }));
                        }
                      }}
                      className="w-full px-3 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text"
                    >
                      <option value="">
                        {commissionTemplates.length === 0
                          ? 'No templates available'
                          : 'Select a template...'}
                      </option>
                      {commissionTemplates.map((t) => (
                        <option key={t.template_id} value={t.template_id}>
                          {t.name} (T1: {t.tier1_percentage}% • T2: {t.tier2_percentage}% • T3:{' '}
                          {t.tier3_percentage}%)
                        </option>
                      ))}
                    </select>
                    {commissionTemplates.length === 0 && (
                      <p className="text-xs text-boss-text-muted mt-1">
                        Create templates in Admin → Commission Settings
                      </p>
                    )}
                  </div>

                  {/* Revenue & Cost */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-boss-text-secondary block mb-1">
                        Deal Revenue ($) *
                      </label>
                      {/* Revenue source: actual delivered revenue vs manual */}
                      <div className="flex gap-1 mb-1.5">
                        <button
                          type="button"
                          onClick={() =>
                            setCommissionForm((f) => ({
                              ...f,
                              revenue_source: 'contract_actuals',
                              revenue:
                                accruedRevenue && accruedRevenue.revenue != null
                                  ? accruedRevenue.revenue
                                  : f.revenue,
                            }))
                          }
                          disabled={!accruedRevenue || Number(accruedRevenue.revenue) <= 0}
                          className={`flex-1 px-2 py-1 rounded text-[11px] border transition ${
                            commissionForm.revenue_source === 'contract_actuals'
                              ? 'bg-violet-600 border-violet-600 text-white'
                              : 'bg-boss-elevated border-boss-border text-boss-text-secondary hover:text-boss-text'
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          Actual delivered
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setCommissionForm((f) => ({ ...f, revenue_source: 'manual' }))
                          }
                          className={`flex-1 px-2 py-1 rounded text-[11px] border transition ${
                            commissionForm.revenue_source === 'manual'
                              ? 'bg-violet-600 border-violet-600 text-white'
                              : 'bg-boss-elevated border-boss-border text-boss-text-secondary hover:text-boss-text'
                          }`}
                        >
                          Enter manually
                        </button>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        value={commissionForm.revenue}
                        readOnly={commissionForm.revenue_source === 'contract_actuals'}
                        onChange={(e) =>
                          setCommissionForm((f) => ({ ...f, revenue: e.target.value }))
                        }
                        className={`w-full px-3 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text ${
                          commissionForm.revenue_source === 'contract_actuals'
                            ? 'opacity-70 cursor-not-allowed'
                            : ''
                        }`}
                      />
                      <p className="text-[10px] text-boss-text-muted mt-1">
                        {accruedLoading
                          ? 'Checking delivered revenue…'
                          : accruedRevenue && Number(accruedRevenue.revenue) > 0
                            ? `Delivered so far: ${fmt(accruedRevenue.revenue)} from ${
                                accruedRevenue.application_count || 0
                              } approved ${
                                (accruedRevenue.application_count || 0) === 1 ? 'entry' : 'entries'
                              }`
                            : 'No delivered revenue yet — enter manually'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-boss-text-secondary block mb-1">Cost</label>
                      <div className="flex gap-2">
                        <select
                          value={commissionForm.cost_type}
                          onChange={(e) =>
                            setCommissionForm((f) => ({ ...f, cost_type: e.target.value }))
                          }
                          className="w-20 px-2 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text"
                        >
                          <option value="percentage">%</option>
                          <option value="actual">$</option>
                        </select>
                        <input
                          type="number"
                          step="0.01"
                          value={commissionForm.cost_value}
                          onChange={(e) =>
                            setCommissionForm((f) => ({ ...f, cost_value: e.target.value }))
                          }
                          className="flex-1 px-3 py-2 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text"
                        />
                      </div>
                      <p className="text-[10px] text-boss-text-muted mt-1">
                        Actual cost: {fmt(actualCost)}
                      </p>
                    </div>
                  </div>

                  {/* Tier Percentages */}
                  <div className="bg-violet-900/10 border border-violet-800/30 rounded-lg p-3 space-y-2">
                    <h4 className="text-xs font-semibold text-violet-700 dark:text-violet-300">
                      Tier Percentages
                    </h4>
                    {commissionForm.tiers.map((tier, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs text-boss-text-secondary w-24">
                          {tier.tier_name}:
                        </span>
                        <input
                          type="number"
                          step="0.1"
                          value={tier.percentage}
                          onChange={(e) => {
                            const updated = [...commissionForm.tiers];
                            updated[idx].percentage = parseFloat(e.target.value) || 0;
                            setCommissionForm((f) => ({ ...f, tiers: updated }));
                          }}
                          className="w-20 px-2 py-1 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text"
                        />
                        <span className="text-xs text-boss-text-secondary">%</span>
                      </div>
                    ))}
                  </div>

                  {/* User Assignment */}
                  {commissionForm.tiers.map((tier, tierIdx) => (
                    <div
                      key={tierIdx}
                      className="bg-boss-accent-muted border border-boss-border rounded-lg p-3"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-semibold text-boss-text">
                          Tier {tier.tier_number}: {tier.tier_name}
                        </h4>
                        <button
                          onClick={() => {
                            const updated = [...commissionForm.tiers];
                            updated[tierIdx].users = [
                              ...(updated[tierIdx].users || []),
                              { user_id: '', user_name: '', split_percentage: 100 },
                            ];
                            setCommissionForm((f) => ({ ...f, tiers: updated }));
                          }}
                          className="text-xs text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:text-violet-300 flex items-center gap-1"
                        >
                          <Plus size={12} /> Add User
                        </button>
                      </div>
                      {tier.users && tier.users.length > 0 ? (
                        <div className="space-y-2">
                          {tier.users.map((u, userIdx) => (
                            <div key={userIdx} className="flex items-center gap-2">
                              <select
                                value={u.user_id}
                                onChange={(e) => {
                                  const updated = [...commissionForm.tiers];
                                  updated[tierIdx].users[userIdx].user_id = e.target.value;
                                  const user = users.find((usr) => usr.user_id === e.target.value);
                                  updated[tierIdx].users[userIdx].user_name = user ? user.name : '';
                                  setCommissionForm((f) => ({ ...f, tiers: updated }));
                                }}
                                className="flex-1 px-2 py-1 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text"
                              >
                                <option value="">Select user...</option>
                                {users.map((usr) => (
                                  <option key={usr.user_id} value={usr.user_id}>
                                    {usr.name}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                step="1"
                                value={u.split_percentage}
                                onChange={(e) => {
                                  const updated = [...commissionForm.tiers];
                                  updated[tierIdx].users[userIdx].split_percentage =
                                    parseFloat(e.target.value) || 0;
                                  setCommissionForm((f) => ({ ...f, tiers: updated }));
                                }}
                                className="w-16 px-2 py-1 bg-boss-elevated border border-boss-border rounded text-xs text-boss-text"
                              />
                              <span className="text-xs text-boss-text-secondary">%</span>
                              <button
                                onClick={() => {
                                  const updated = [...commissionForm.tiers];
                                  updated[tierIdx].users = updated[tierIdx].users.filter(
                                    (_, i) => i !== userIdx,
                                  );
                                  setCommissionForm((f) => ({ ...f, tiers: updated }));
                                }}
                                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-300"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-boss-text-muted italic">No users assigned yet</p>
                      )}
                    </div>
                  ))}

                  {/* Live Calculation */}
                  <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/30 rounded-lg p-3 space-y-2">
                    <h4 className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                      Live Calculation
                    </h4>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-boss-text-secondary">Revenue:</span>
                        <span className="text-boss-text">{fmt(revenue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-boss-text-secondary">Cost:</span>
                        <span className="text-red-600 dark:text-red-400">-{fmt(actualCost)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-boss-text-secondary">Tier 1 Commission:</span>
                        <span className="text-blue-600 dark:text-blue-400">
                          -{fmt(tier1Commission)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-boss-text-secondary">Tier 2 Commission:</span>
                        <span className="text-amber-600 dark:text-amber-400">
                          -{fmt(tier2Commission)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-boss-text-secondary">Tier 3 Commission:</span>
                        <span className="text-purple-400">-{fmt(tier3Commission)}</span>
                      </div>
                      <div className="border-t border-boss-border pt-2 flex justify-between">
                        <span className="text-emerald-700 dark:text-emerald-300 font-medium">
                          Profit After Commission:
                        </span>
                        <span className="text-emerald-700 dark:text-emerald-300 font-bold">
                          {fmt(profitAfterCommission)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-emerald-700 dark:text-emerald-300 font-medium">
                          Profit Margin:
                        </span>
                        <span className="text-emerald-700 dark:text-emerald-300 font-bold">
                          {profitPercentage.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setCommissionDeal(null)}
                      className="flex-1 px-3 py-2 bg-boss-elevated hover:bg-boss-surface text-boss-text shadow-sm text-xs rounded-lg transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveCommissionPlan}
                      disabled={commissionLoading}
                      className="flex-1 px-3 py-2 bg-violet-600 hover:bg-violet-500 text-white text-xs rounded-lg transition disabled:opacity-50 font-medium"
                    >
                      {commissionLoading ? 'Committing…' : 'Commit Commission Plan'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
